import { normalizeEventUuidList, normalizePartLabel } from './memberEventPartUtils';

const pickFirstStringDefault = (...values) => {
  for (const val of values) {
    if (typeof val === 'string' && val.trim()) return val.trim();
  }
  return '';
};

export const buildParticipantEventMembershipLabel = ({
  eventUuids,
  targetGroupEvents = [],
  participantGroupEventOrderMap = new Map(),
  participantGroupEventMap = new Map(),
  pickFirstString = pickFirstStringDefault,
}) => {
  if (!Array.isArray(targetGroupEvents) || targetGroupEvents.length <= 1) return '';

  const targetEventUuidSet = new Set(
    targetGroupEvents
      .map((event) => String(event?.event_uuid || '').trim())
      .filter(Boolean)
  );
  const normalized = normalizeEventUuidList(eventUuids)
    .filter((eventUuid) => targetEventUuidSet.has(String(eventUuid)));
  if (!normalized.length) return '';

  const targetGroupEventMap = new Map();
  targetGroupEvents.forEach((event) => {
    const eventUuid = String(event?.event_uuid || '').trim();
    if (!eventUuid || targetGroupEventMap.has(eventUuid)) return;
    targetGroupEventMap.set(eventUuid, event);
  });

  const sorted = [...normalized].sort((a, b) => {
    const orderA = participantGroupEventOrderMap.get(String(a));
    const orderB = participantGroupEventOrderMap.get(String(b));
    if (orderA !== orderB) return orderA - orderB;
    return String(a).localeCompare(String(b), 'en');
  });
  if (sorted.length >= targetGroupEvents.length) return '@全公演';

  const labels = sorted
    .map((eventUuid) => {
      const event =
        participantGroupEventMap.get(String(eventUuid))
        || targetGroupEventMap.get(String(eventUuid))
        || null;
      if (!event) return '';
      const performanceType = pickFirstString(event.performance_type);
      if (performanceType) return `@${performanceType}`;
      const eventName = pickFirstString(event.name);
      return eventName ? `@${eventName}` : '';
    })
    .filter(Boolean);
  if (!labels.length) return '';
  return labels.join(', ');
};

export const buildParticipantEventPartSummary = ({
  artist,
  fallbackPartLabel = 'パート未設定',
  selectedEventId = '',
  participantGroupEventOrderMap = new Map(),
  participantGroupEventMap = new Map(),
  pickFirstString = pickFirstStringDefault,
  isUnifiedEventGroupChat = false,
}) => {
  const normalizedFallbackPartLabel = normalizePartLabel(fallbackPartLabel) || 'パート未設定';
  if (!artist) {
    return { partLabel: normalizedFallbackPartLabel, eventPartLabel: '', isMixed: false };
  }

  if (!isUnifiedEventGroupChat) {
    return { partLabel: normalizedFallbackPartLabel, eventPartLabel: '', isMixed: false };
  }

  const targetEventUuids = normalizeEventUuidList(
    artist?.event_uuids || (selectedEventId ? [selectedEventId] : [])
  ).filter((eventUuid) => participantGroupEventOrderMap.has(String(eventUuid)));

  if (!targetEventUuids.length) {
    return { partLabel: normalizedFallbackPartLabel, eventPartLabel: '', isMixed: false };
  }

  const sortedEventUuids = [...targetEventUuids].sort((a, b) => {
    const orderA = participantGroupEventOrderMap.get(String(a));
    const orderB = participantGroupEventOrderMap.get(String(b));
    if (orderA !== orderB) return orderA - orderB;
    return String(a).localeCompare(String(b), 'en');
  });

  const assignmentMap = new Map();
  const assignments = Array.isArray(artist?.event_part_assignments) ? artist.event_part_assignments : [];
  assignments.forEach((assignment) => {
    const eventUuid = String(assignment?.event_uuid || '').trim();
    if (!eventUuid) return;
    const partLabel = normalizePartLabel(
      pickFirstString(assignment?.part_label, assignment?.part, assignment?.label)
    );
    const hasAssignment = assignment?.has_assignment === true
      || assignment?.casting_info_id != null
      || Boolean(partLabel);
    assignmentMap.set(eventUuid, { partLabel, hasAssignment });
  });

  const eventPartEntries = sortedEventUuids.map((eventUuid) => {
    const eventInfo = participantGroupEventMap.get(String(eventUuid));
    const eventScopeLabel = pickFirstString(eventInfo?.performance_type, eventInfo?.name) || String(eventUuid);
    const assignment = assignmentMap.get(String(eventUuid));
    const partLabel = normalizePartLabel(assignment?.partLabel);
    const hasAssignment = assignment?.hasAssignment === true;
    return {
      eventScopeLabel: `@${eventScopeLabel}`,
      partLabel: partLabel || normalizedFallbackPartLabel,
      hasAssignment,
    };
  });

  if (eventPartEntries.length === 1) {
    const only = eventPartEntries[0];
    return {
      partLabel: only.hasAssignment ? only.partLabel : normalizedFallbackPartLabel,
      eventPartLabel: '',
      isMixed: false,
    };
  }

  const configuredPartLabels = Array.from(
    new Set(
      eventPartEntries
        .map((entry) => entry.partLabel)
        .filter(Boolean)
    )
  );
  const isMixed = configuredPartLabels.length > 1;

  const partLabel = isMixed
    ? normalizedFallbackPartLabel
    : (configuredPartLabels[0] || normalizedFallbackPartLabel);
  const eventPartLabel = isMixed
    ? eventPartEntries.map((entry) => `${entry.eventScopeLabel}: ${entry.partLabel}`).join(', ')
    : '';

  return {
    partLabel: partLabel || normalizedFallbackPartLabel,
    eventPartLabel,
    isMixed,
  };
};

export const resolveAllPerformancesPartBucket = ({
  partSummary = null,
  fallbackPartLabel = 'パート未設定',
  fallbackOrder = null,
  mixedOrder = null,
  mixedLabel = '公演別',
  resolveCommonPartOrder,
}) => {
  const normalizedFallbackPartLabel = normalizePartLabel(fallbackPartLabel) || 'パート未設定';
  const resolvedPartLabel = normalizePartLabel(partSummary?.partLabel) || normalizedFallbackPartLabel;
  const resolvedEventPartLabel = normalizePartLabel(partSummary?.eventPartLabel);
  const isMixed = partSummary?.isMixed === true || Boolean(resolvedEventPartLabel);

  let sectionOrder = fallbackOrder;
  if (isMixed) {
    sectionOrder = mixedOrder;
  } else if (typeof resolveCommonPartOrder === 'function') {
    const candidateOrder = resolveCommonPartOrder({
      partLabel: resolvedPartLabel,
    });
    if (Number.isFinite(candidateOrder)) {
      sectionOrder = candidateOrder;
    }
  }

  return {
    sectionLabel: isMixed ? mixedLabel : resolvedPartLabel,
    sectionOrder,
    partLabel: resolvedPartLabel,
    eventPartLabel: isMixed ? resolvedEventPartLabel : '',
    isMixed,
  };
};
